/**
 * Pure scoring engine for completed maneuvers.
 *
 * Inputs are a {@link ScoreInput} snapshot collected by `maneuver.store` and
 * the validated `scoring.json` config. Output is a {@link ScoreBreakdown}
 * suitable for display in the result modal.
 *
 * The formula matches /memories/session/plan.md section E:
 *
 *   final = round(
 *     baseXp × difficultyMul × performance
 *     + timeBonus + firstAttemptBonus
 *     − collisionPenalty − dragPenalty − tensionPenalty − overtimePenalty
 *   )
 *
 * A severe collision (`maxContactSpeedMs ≥ severeCollisionSpeedMs`) marks the
 * maneuver as failed and forces the final score to 0 regardless of other
 * contributions. This is a hard rule rather than a soft penalty so players
 * can't "earn through" reckless approaches.
 */

import type { DifficultyTierId, ScoringConfig } from "./gamification.types";

export interface ScoreInput {
  readonly baseXp: number;
  readonly tier: DifficultyTierId;
  /** Performance metrics in [0, 1], one per weight key in scoring.performance.weights. */
  readonly performanceMetrics: Readonly<Record<string, number>>;
  readonly elapsedSec: number;
  readonly targetTimeSec: number;
  readonly timeLimitSec: number;
  readonly isFirstAttempt: boolean;
  /** Worst contact speed of any collision during the maneuver (m/s). */
  readonly maxContactSpeedMs: number;
  /** Seconds the anchor was in `dragging` state. */
  readonly anchorDragSec: number;
  /** Number of distinct lines whose tension ratio exceeded the threshold. */
  readonly overTensionedLineCount: number;
  /**
   * How many times the player shifted the outboard gear while the throttle
   * magnitude was above the high-RPM threshold. Each event costs
   * `scoring.penalties.gearShiftHighRpmPenaltyXp`.
   */
  readonly highRpmGearShiftCount: number;
}

export interface ScoreBreakdown {
  readonly finalScore: number;
  readonly failed: boolean;
  readonly contributions: {
    readonly baseXp: number;
    readonly difficultyMultiplier: number;
    readonly performanceScale: number;
    readonly scaledBase: number;
    readonly timeBonus: number;
    readonly firstAttemptBonus: number;
    readonly collisionPenalty: number;
    readonly dragPenalty: number;
    readonly tensionPenalty: number;
    readonly overtimePenalty: number;
    readonly gearShiftPenalty: number;
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Convert weighted metrics in [0,1] to a multiplier in [scaleMin, scaleMax]. */
function computePerformanceScale(
  metrics: Readonly<Record<string, number>>,
  cfg: ScoringConfig["performance"],
): number {
  let weightedSum = 0;
  let totalWeight = 0;
  for (const [key, weight] of Object.entries(cfg.weights)) {
    const raw = metrics[key];
    // Missing metric → treat as a neutral mid-range value (0.5). This keeps
    // partial telemetry from collapsing the score to scaleMin.
    const value = typeof raw === "number" ? clamp(raw, 0, 1) : 0.5;
    weightedSum += value * weight;
    totalWeight += weight;
  }
  const normalized = totalWeight > 0 ? weightedSum / totalWeight : 0.5;
  return cfg.scaleMin + (cfg.scaleMax - cfg.scaleMin) * normalized;
}

function computeTimeBonus(
  baseXp: number,
  elapsedSec: number,
  targetTimeSec: number,
  bands: ScoringConfig["timeBonus"],
): number {
  if (targetTimeSec <= 0) return 0;
  const ratio = elapsedSec / targetTimeSec;
  // Bands are checked in ascending tightness — the strictest band that still
  // matches wins, so we sort ascending by ifUnderRatio and pick the smallest
  // ratio that beats us.
  const sorted = [...bands].sort((a, b) => a.ifUnderRatio - b.ifUnderRatio);
  let best = 0;
  for (const band of sorted) {
    if (ratio < band.ifUnderRatio) {
      best = Math.max(best, band.baseXpBonusRatio);
    }
  }
  return baseXp * best;
}

function computeOvertimePenalty(
  elapsedSec: number,
  targetTimeSec: number,
  perTenSec: number,
): number {
  if (elapsedSec <= targetTimeSec) return 0;
  const overtime = elapsedSec - targetTimeSec;
  return Math.floor(overtime / 10) * perTenSec;
}

function computeCollisionPenalty(maxContactSpeedMs: number): number {
  // Quadratic in contact speed (matches `ImpactScoreStore` philosophy):
  // 0.5 m/s → 5 XP, 1.0 m/s → 20 XP, 1.5 m/s → triggers failure path.
  if (maxContactSpeedMs <= 0) return 0;
  return Math.round(20 * maxContactSpeedMs * maxContactSpeedMs);
}

/**
 * Compute the final score and breakdown for a completed maneuver.
 *
 * If `maxContactSpeedMs >= scoring.penalties.severeCollisionSpeedMs` the
 * maneuver is considered failed: `finalScore = 0`, `failed = true`. All
 * contributions are still computed and returned so the result modal can
 * display "what would have been".
 */
export function computeFinalScore(
  input: ScoreInput,
  scoring: ScoringConfig,
): ScoreBreakdown {
  const tier = scoring.difficultyTiers[input.tier];
  if (!tier) {
    throw new Error(`computeFinalScore: unknown tier '${input.tier}'`);
  }

  const difficultyMultiplier = tier.multiplier;
  const performanceScale = computePerformanceScale(
    input.performanceMetrics,
    scoring.performance,
  );
  const scaledBase = input.baseXp * difficultyMultiplier * performanceScale;

  const timeBonus = computeTimeBonus(
    input.baseXp,
    input.elapsedSec,
    input.targetTimeSec,
    scoring.timeBonus,
  );
  const firstAttemptBonus = input.isFirstAttempt
    ? input.baseXp * scoring.firstAttemptBonusRatio
    : 0;

  const collisionPenalty = computeCollisionPenalty(input.maxContactSpeedMs);
  const dragPenalty =
    Math.max(0, input.anchorDragSec) *
    scoring.penalties.anchorDragSecMultiplier;
  const tensionPenalty =
    Math.max(0, input.overTensionedLineCount) *
    scoring.penalties.lineTensionPenaltyXp;
  const overtimePenalty = computeOvertimePenalty(
    input.elapsedSec,
    input.targetTimeSec,
    scoring.overtimePenaltyPerTenSec,
  );
  const gearShiftPenalty =
    Math.max(0, input.highRpmGearShiftCount) *
    scoring.penalties.gearShiftHighRpmPenaltyXp;

  const failed =
    input.maxContactSpeedMs >= scoring.penalties.severeCollisionSpeedMs;

  const raw =
    scaledBase +
    timeBonus +
    firstAttemptBonus -
    collisionPenalty -
    dragPenalty -
    tensionPenalty -
    overtimePenalty -
    gearShiftPenalty;

  const finalScore = failed ? 0 : Math.max(0, Math.round(raw));

  return {
    finalScore,
    failed,
    contributions: {
      baseXp: input.baseXp,
      difficultyMultiplier,
      performanceScale,
      scaledBase,
      timeBonus,
      firstAttemptBonus,
      collisionPenalty,
      dragPenalty,
      tensionPenalty,
      overtimePenalty,
      gearShiftPenalty,
    },
  };
}
